import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { authFetch, isAuthenticated } from '../utils/auth';
import { toast } from 'sonner';
import { Activity, CheckCircle, Clock, RefreshCw, Calendar, User, Search, X, Truck, Package, Droplet, ChevronDown, PawPrint, Dog, Cat, Heart } from 'lucide-react';
import { getErrorMessage } from '../utils/errors';

const PetTracking = () => {
  const navigate = useNavigate();
  const [petsData, setPetsData] = useState(null);
  const [filteredPets, setFilteredPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const hideTestPets = true;
  const [petEvents, setPetEvents] = useState({});
  const [expandedPets, setExpandedPets] = useState(new Set());
  const [mobileStatsOpen, setMobileStatsOpen] = useState(false);
  const [preBookedOpen, setPreBookedOpen] = useState(false);
  const intervalRef = useRef(null);
  const eventsIntervalRef = useRef(null);

  const fetchPetsStatus = async (showToast = false, isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      
      const response = await authFetch('/api/pets/status');
      if (response.ok) {
        const data = await response.json();
        setPetsData(data);
        setFilteredPets(data.pets || []);
        setLastUpdated(new Date());
        if (showToast) {
          toast.success('Pet status updated');
        }
      } else {
        toast.error('Failed to load pet status');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
      if (isManual) {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  };

  const fetchRecentEvents = async () => {
    try {
      const response = await authFetch('/api/events/recent');
      if (response.ok) {
        const data = await response.json();
        const newEvents = data.events || [];
        
        // Group events by pet_id for timeline display (show ALL events, read or not)
        const eventsByPet = {};
        newEvents.forEach(event => {
          const petId = event.pet_id;
          if (!eventsByPet[petId]) {
            eventsByPet[petId] = [];
          }
          eventsByPet[petId].push(event);
          
          // Show toast ONLY for unread events
          if (!event.read && event.event_type) {
            const eventIcons = {
              'pet_collected_from_clinic': '🚗',
              'pet_in_care': '🏥',
              'pet_cremation_started': '💧',
              'pet_completed': '✅',
              'ashes_delivered_to_clinic': '⚱️',
              'redemption_processed': '💰',
              'product_order_fulfilled': '📦',
              'material_request_fulfilled': '📋'
            };
            
            const icon = eventIcons[event.event_type] || '📬';
            
            // Special handling for redemption events
            if (event.event_type === 'redemption_processed') {
              toast.success(`${icon} ${event.message}`, {
                duration: 8000,
                description: 'Funds transferred to your bank account'
              });
            } else if (event.event_type === 'product_order_fulfilled') {
              toast.success(`${icon} ${event.message}`, {
                duration: 8000,
                description: 'Your product order has been dispatched!'
              });
            } else if (event.event_type === 'material_request_fulfilled') {
              toast.success(`${icon} ${event.message}`, {
                duration: 8000,
                description: 'Your material request has been completed and dispatched!'
              });
            } else {
              toast.success(`${icon} ${event.message}`, {
                duration: 5000,
                description: event.pet_name
              });
            }
          }
        });
        
        setPetEvents(eventsByPet);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };


  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    fetchPetsStatus();
    fetchRecentEvents(); // Initial fetch

    // Poll for pet status every 30 seconds
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchPetsStatus();
      }, 30000);
    }
    
    // Poll for events every 10 seconds (real-time updates)
    eventsIntervalRef.current = setInterval(() => {
      fetchRecentEvents();
    }, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (eventsIntervalRef.current) {
        clearInterval(eventsIntervalRef.current);
      }
    };
  }, [navigate, autoRefresh]);

  // Search filter across multiple fields + test pet filter
  useEffect(() => {
    if (!petsData || !petsData.pets) return;
    
    let pets = petsData.pets;
    
    // Filter out test pets if enabled
    if (hideTestPets) {
      pets = pets.filter(pet => {
        const name = (pet.pet_name || '').toLowerCase();
        const clientName = (pet.client_name || '').toLowerCase();
        return !(
          name.includes('autotest') ||
          clientName.includes('autotest') ||
          name === 'test' ||
          name === 'dummy' ||
          name === 'sample' ||
          name.startsWith('test_') ||
          name.startsWith('autotest')
        );
      });
    }
    
    if (!searchQuery.trim()) {
      setFilteredPets(pets);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = pets.filter(pet => {
      return (
        pet.pet_name?.toLowerCase().includes(query) ||
        pet.client_name?.toLowerCase().includes(query) ||
        pet.phone_number?.includes(query) ||
        pet.breed?.toLowerCase().includes(query) ||
        pet.status?.toLowerCase().includes(query) ||
        pet.workflow_stage?.toLowerCase().includes(query)
      );
    });
    
    setFilteredPets(filtered);
  }, [searchQuery, petsData, hideTestPets]);

  const getStatusInfo = (workflowStage, pet = null) => {
    const ashesToVet = pet?.ashes_delivery_to_vet === true;
    
    const statusMap = {
      'prepayment': {
        label: 'Pre-booked',
        description: 'Customer has pre-paid. Pet not yet ready for collection.',
        nextStep: 'No action needed — Pure Souls will coordinate collection with the owner when the time comes.',
        className: 'bg-muted text-muted-foreground border-muted-foreground/30',
        barColor: 'bg-muted-foreground/30',
        icon: Clock
      },
      'pending': {
        label: 'Awaiting Collection',
        description: 'Pet scheduled for collection by Pure Souls',
        nextStep: 'Pure Souls will collect the pet soon',
        className: 'bg-[hsl(var(--warning-light))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]',
        barColor: 'bg-[hsl(var(--warning))]',
        icon: Clock
      },
      'collected': {
        label: 'Collected - In Transit',
        description: 'Pet collected by our driver, on the way to Pure Souls',
        nextStep: 'Pet will arrive at our facility soon',
        className: 'bg-[hsl(var(--info-light))] text-[hsl(var(--info))] border-[hsl(var(--info))]',
        barColor: 'bg-[hsl(var(--info))]',
        icon: Truck
      },
      'in_care': {
        label: 'In Our Care',
        description: 'Pet is with Pure Souls, being cared for',
        nextStep: 'Water cremation will begin soon',
        className: 'bg-[hsl(var(--info-light))] text-[hsl(var(--info))] border-[hsl(var(--info))]',
        barColor: 'bg-[hsl(var(--info))]',
        icon: Heart
      },
      'cremation': {
        label: 'Aquacremation Underway',
        description: 'Pet is currently undergoing water cremation (aquacremation)',
        nextStep: ashesToVet ? 'Ashes will be prepared and delivered to your clinic' : 'Service will be complete soon',
        className: 'bg-[hsl(var(--info-light))] text-[hsl(var(--info))] border-[hsl(var(--info))]',
        barColor: 'bg-[hsl(var(--info))]',
        icon: Droplet
      },
      'cremation_in_progress': {
        label: 'Aquacremation Underway',
        description: 'Pet is currently undergoing water cremation (aquacremation)',
        nextStep: ashesToVet ? 'Ashes will be prepared and delivered to your clinic' : 'Service will be complete soon',
        className: 'bg-[hsl(var(--info-light))] text-[hsl(var(--info))] border-[hsl(var(--info))]',
        barColor: 'bg-[hsl(var(--info))]',
        icon: Droplet
      },
      'awaiting_delivery_to_vet': {
        label: 'Ashes Ready — Delivery Coming',
        description: 'Aquacremation complete, ashes being prepared for delivery to your clinic',
        nextStep: 'Ashes will be delivered to your clinic soon',
        className: 'bg-[hsl(var(--success-light))] text-[hsl(var(--success))] border-[hsl(var(--success))]',
        barColor: 'bg-[hsl(var(--success))]',
        icon: Package
      },
      'delivered_to_vet': {
        label: 'Ashes Delivered',
        description: 'Ashes have been returned to your clinic',
        nextStep: 'Ready for owner to collect from your clinic',
        className: 'bg-[hsl(var(--success-light))] text-[hsl(var(--success))] border-[hsl(var(--success))]',
        barColor: 'bg-[hsl(var(--success))]',
        icon: Package
      },
      'ashes_ready': {
        label: 'Ashes Ready',
        description: 'Cremation complete, ashes ready for delivery',
        nextStep: ashesToVet ? 'Ashes will be delivered to your clinic soon' : 'Service will be complete soon',
        className: 'bg-[hsl(var(--success-light))] text-[hsl(var(--success))] border-[hsl(var(--success))]',
        barColor: 'bg-[hsl(var(--success))]',
        icon: CheckCircle
      },
      'completed': {
        label: 'Completed',
        description: 'Service complete',
        nextStep: 'All done! Thank you for choosing Pure Souls.',
        className: 'bg-[hsl(var(--success-light))] text-[hsl(var(--success))] border-[hsl(var(--success))]',
        barColor: 'bg-[hsl(var(--success))]',
        icon: CheckCircle
      },
      'delivered': {
        label: 'Ashes Delivered',
        description: 'Ashes have been returned to your clinic',
        nextStep: 'Ready for owner to collect from your clinic',
        className: 'bg-[hsl(var(--success-light))] text-[hsl(var(--success))] border-[hsl(var(--success))]',
        barColor: 'bg-[hsl(var(--success))]',
        icon: Package
      }
    };

    return statusMap[workflowStage] || statusMap['pending'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // CRM API timestamps are UTC but missing the "Z" suffix.
  // JavaScript parses "2026-03-02T08:24:09" as LOCAL time without Z.
  // This helper ensures they're treated as UTC.
  const ensureUTC = (ts) => {
    if (!ts) return ts;
    if (!ts.includes('T')) return ts; // date-only
    if (ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts)) return ts; // already has tz
    return ts + 'Z';
  };

  // Format a webhook event's timestamp for display
  // Prefers timestamp_aest (Townsville time, display directly)
  // Falls back to timestamp (UTC) converted to AEST
  const formatEventTime = (event) => {
    if (!event) return 'N/A';
    const aest = event.timestamp_aest;
    if (aest) {
      const date = new Date(aest);
      return date.toLocaleDateString('en-AU', {
        year: 'numeric', month: 'short', day: 'numeric',
        timeZone: 'Australia/Brisbane'
      }) + ', ' + date.toLocaleTimeString('en-AU', {
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: 'Australia/Brisbane'
      });
    }
    const ts = ensureUTC(event.timestamp);
    if (!ts) return 'N/A';
    const date = new Date(ts);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric', month: 'short', day: 'numeric',
      timeZone: 'Australia/Brisbane'
    }) + ', ' + date.toLocaleTimeString('en-AU', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'Australia/Brisbane'
    });
  };

  // Format a CRM API timestamp (UTC without Z) to AEST for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    // Date-only strings (e.g., "2026-03-02") — only show date, no time
    if (!dateString.includes('T')) {
      return formatDate(dateString);
    }
    const date = new Date(ensureUTC(dateString));
    return date.toLocaleDateString('en-AU', {
      year: 'numeric', month: 'short', day: 'numeric',
      timeZone: 'Australia/Brisbane'
    }) + ', ' + date.toLocaleTimeString('en-AU', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'Australia/Brisbane'
    });
  };

  // Format a local AEST time (e.g. planned_delivery_datetime) — no UTC conversion
  const formatLocalDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    if (!dateString.includes('T')) return formatDate(dateString);
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    const date = new Date(year, month - 1, day, hours, minutes);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric', month: 'short', day: 'numeric',
    }) + ', ' + date.toLocaleTimeString('en-AU', {
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };

  // Recalculate stats from the pets list using timestamp-driven logic
  // (CRM's own counters can be out of sync with our timestamp-based display)
  const stats = petsData ? (() => {
    const pets = petsData.pets || [];
    let inCare = 0, completed = 0, prepayment = 0;
    pets.forEach(pet => {
      if (pet.workflow_stage === 'prepayment') { prepayment++; return; }
      const ashesToVet = pet.ashes_delivery_to_vet === true;
      if (pet.completed_at || (ashesToVet && pet.delivered_at)) {
        completed++;
      } else if (pet.cremation_started_at || pet.in_care_at || pet.collected_at) {
        inCare++;
      }
    });
    const activePets = pets.length - prepayment;
    return [
      { label: 'Active Pets', value: activePets, icon: Activity, color: 'primary' },
      { label: 'In Our Care', value: inCare, icon: Activity, color: 'info' },
      { label: 'Completed', value: completed, icon: CheckCircle, color: 'success' },
      { label: 'Pre-booked', value: prepayment, icon: Clock, color: 'muted' },
    ];
  })() : [];

  return (
    <Layout>
      <div className="space-y-8" data-testid="pet-tracking-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row md:items-start md:justify-between gap-4"
        >
          <div>
            <h1 className="text-4xl font-bold mb-2 text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Pet Status Tracking
            </h1>
            <p className="text-muted-foreground text-lg">
              Real-time status of your referred pets
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => fetchPetsStatus(true, true)}
              disabled={refreshing || loading}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </motion.div>

        {/* Search Bar */}
        {!loading && petsData && petsData.pets?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                    <Input
                      placeholder="Search by pet name, owner, phone, breed, or status..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-10 h-12"
                      data-testid="pet-search-input"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                  <span />
                </div>
                {searchQuery && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Showing {filteredPets.length} of {petsData.pets?.length || 0} pets
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Overview — collapsible on mobile, always visible on desktop */}
        {!loading && stats.length > 0 && (
          <>
            {/* Mobile: collapsible toggle */}
            <div className="md:hidden">
              <button
                type="button"
                onClick={() => setMobileStatsOpen(!mobileStatsOpen)}
                className="w-full flex items-center justify-between p-4 rounded-2xl border-0 shadow-md bg-card cursor-pointer hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {stats[0].value} Pets · {stats[1].value} In Our Care · {stats[2].value} Completed
                  </span>
                </div>
                <motion.div animate={{ rotate: mobileStatsOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                </motion.div>
              </button>
              <AnimatePresence>
                {mobileStatsOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      {stats.map((stat) => (
                        <Card key={stat.label} className="border-0 shadow-md">
                          <CardContent className="p-4 text-center">
                            <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
                            <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>{stat.value}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop: always visible */}
            <div className="hidden md:grid md:grid-cols-3 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + (index * 0.1) }}
                >
                  <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{stat.label}</p>
                          <p className="text-3xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                            {stat.value}
                          </p>
                        </div>
                        <div className={`p-3 rounded-xl ${
                          stat.color === 'primary' ? 'bg-primary/10' :
                          stat.color === 'info' ? 'bg-[hsl(var(--info-light))]' :
                          'bg-[hsl(var(--success-light))]'
                        }`}>
                          <stat.icon className={`h-6 w-6 ${
                            stat.color === 'primary' ? 'text-primary' :
                            stat.color === 'info' ? 'text-[hsl(var(--info))]' :
                            'text-[hsl(var(--success))]'
                          }`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* Auto-refresh Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="flex items-center gap-3 text-sm"
        >
          <Checkbox
            id="auto-refresh"
            checked={autoRefresh}
            onCheckedChange={setAutoRefresh}
            data-testid="auto-refresh-checkbox"
          />
          <Label htmlFor="auto-refresh" className="cursor-pointer">
            Auto-refresh every 30 seconds
          </Label>
          {lastUpdated && (
            <span className="text-muted-foreground ml-auto">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </motion.div>

        {/* Pets List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 skeleton rounded-2xl" />
            ))}
          </div>
        ) : !petsData || petsData.pets?.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="border-0 shadow-md">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="p-4 rounded-full bg-muted mb-4">
                  <Activity className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  No Pets Yet
                </h3>
                <p className="text-muted-foreground text-center mb-6">
                  You haven&apos;t referred any pets yet. Submit your first collection request to get started.
                </p>
                <Button onClick={() => navigate('/requests/new')}>
                  Submit Collection Request
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : filteredPets.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="border-0 shadow-md">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Search className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  No Results Found
                </h3>
                <p className="text-muted-foreground text-center mb-4">
                  No pets match &quot;{searchQuery}&quot;
                </p>
                <Button variant="outline" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {filteredPets.filter(p => p.workflow_stage !== 'prepayment').map((pet, index) => {
                const petId = pet.pet_id || pet.pet_record_id;
                const events = petEvents[petId] || [];
                
                const ashesToVet = pet.ashes_delivery_to_vet === true;
                const isSelfDelivery = !!pet.planned_delivery_datetime;

                // ========================================
                // STATUS DETERMINATION — TIMESTAMP-DRIVEN
                // Timestamps are the single source of truth for what has actually happened.
                // Exception: 'prepayment' is a reliable CRM-managed stage for pre-paid pets
                // with no timestamps yet — safe to read directly from workflow_stage.
                // ========================================
                let displayStatus = 'pending';

                // Pre-payment: customer paid in advance, pet not yet ready for collection
                if (pet.workflow_stage === 'prepayment') {
                  displayStatus = 'prepayment';
                } else if (pet.completed_at) {
                  displayStatus = 'completed';
                } else if (ashesToVet && pet.delivered_at) {
                  displayStatus = 'completed';
                } else if (pet.cremation_started_at) {
                  displayStatus = 'cremation_in_progress';
                } else if (pet.in_care_at) {
                  displayStatus = 'in_care';
                } else if (pet.collected_at) {
                  displayStatus = 'collected';
                }

                // Webhook events can confirm steps that haven't generated timestamps yet
                if (events.some(e => e.event_type === 'pet_completed')) {
                  displayStatus = 'completed';
                } else if (ashesToVet && events.some(e => e.event_type === 'ashes_delivered_to_clinic')) {
                  displayStatus = 'completed';
                }
                
                const statusInfo = getStatusInfo(displayStatus, pet);
                // Override text for self-delivery pets
                if (isSelfDelivery) {
                  if (displayStatus === 'pending') {
                    statusInfo.label = 'Awaiting Delivery';
                    statusInfo.description = 'Clinic will deliver the pet to Pure Souls';
                    statusInfo.nextStep = 'Awaiting delivery from your clinic';
                  } else if (displayStatus === 'collected') {
                    statusInfo.label = 'Delivered by Clinic';
                    statusInfo.description = 'Pet delivered to Pure Souls by your clinic';
                    statusInfo.nextStep = 'Pet received — In Our Care update coming soon';
                  }
                }
                const StatusIcon = statusInfo.icon;
                const petKey = pet.pet_id || pet.pet_record_id || `pet-${index}`;
                const isExpanded = expandedPets.has(petKey);

                // Build stepper stages
                const stages = [
                  { key: 'collected', label: isSelfDelivery ? 'Delivered' : 'Collected', icon: Truck },
                  { key: 'in_care', label: 'In Our Care', icon: Heart },
                  { key: 'cremation', label: 'Aquacremation', icon: Droplet },
                  ...(ashesToVet ? [{ key: 'delivered', label: 'Delivered', icon: Package }] : []),
                  { key: 'completed', label: 'Completed', icon: CheckCircle },
                ];

                // Map displayStatus to stepper index
                let activeStepIdx = -1;
                if (displayStatus === 'collected') activeStepIdx = 0;
                else if (displayStatus === 'in_care') activeStepIdx = 1;
                else if (displayStatus === 'cremation_in_progress') activeStepIdx = 2;
                else if (ashesToVet && displayStatus === 'delivered_to_vet') activeStepIdx = 3;
                else if (displayStatus === 'completed') activeStepIdx = stages.length - 1;
                else if (displayStatus === 'pending') activeStepIdx = -1;

                const toggleExpand = () => {
                  setExpandedPets(prev => {
                    const next = new Set(prev);
                    if (next.has(petKey)) next.delete(petKey);
                    else next.add(petKey);
                    return next;
                  });
                };
                
                return (
                  <motion.div
                    key={petKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    layout
                    data-testid={`pet-card-${index}`}
                  >
                    <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group"
                      onClick={toggleExpand}
                    >
                      {/* Colored accent bar */}
                      <div className={`h-1.5 ${statusInfo.barColor} transition-all duration-500`} />
                      
                      {/* Always visible: Pet header + details + stepper */}
                      <div className="px-5 pt-4 pb-2">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              displayStatus === 'completed' ? 'bg-[hsl(var(--success))]/10' :
                              displayStatus === 'pending' ? 'bg-[hsl(var(--warning))]/10' :
                              'bg-primary/10'
                            }`}>
                              {pet.pet_type?.toLowerCase() === 'dog' ? (
                                <Dog className={`w-5 h-5 ${displayStatus === 'completed' ? 'text-[hsl(var(--success))]' : displayStatus === 'pending' ? 'text-[hsl(var(--warning))]' : 'text-primary'}`} />
                              ) : pet.pet_type?.toLowerCase() === 'cat' ? (
                                <Cat className={`w-5 h-5 ${displayStatus === 'completed' ? 'text-[hsl(var(--success))]' : displayStatus === 'pending' ? 'text-[hsl(var(--warning))]' : 'text-primary'}`} />
                              ) : (
                                <PawPrint className={`w-5 h-5 ${displayStatus === 'completed' ? 'text-[hsl(var(--success))]' : displayStatus === 'pending' ? 'text-[hsl(var(--warning))]' : 'text-primary'}`} />
                              )}
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-lg font-bold truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                                {pet.pet_name}
                              </h3>
                              <p className="text-xs text-muted-foreground truncate">
                                <User className="h-3 w-3 inline mr-1 -mt-0.5" />
                                {pet.client_name}
                                {pet.phone_number && <span className="ml-2 opacity-60">· {pet.phone_number}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <Badge className={`${statusInfo.className} border font-medium px-3 py-1 text-xs`} data-testid={`pet-status-badge-${index}`}>
                              <StatusIcon className="h-3 w-3 mr-1.5" />
                              {statusInfo.label}
                            </Badge>
                          </div>
                        </div>

                        {/* Pet & Client Detail Chips — always visible */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {pet.breed && (
                            <div className="bg-muted/40 rounded-md px-2.5 py-1">
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mr-1.5">Breed</span>
                              <span className="text-xs font-medium">{pet.breed}</span>
                            </div>
                          )}
                          {pet.weight_kg && (
                            <div className="bg-muted/40 rounded-md px-2.5 py-1">
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mr-1.5">Weight</span>
                              <span className="text-xs font-medium">{pet.weight_kg} kg</span>
                            </div>
                          )}
                          {pet.ashes_delivery_to_vet !== undefined && (
                            <div className="bg-muted/40 rounded-md px-2.5 py-1">
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mr-1.5">Ashes</span>
                              <span className="text-xs font-medium">
                                {pet.ashes_delivery_to_vet
                                  ? (displayStatus === 'completed' ? 'Delivered to your Clinic' : 'To Clinic')
                                  : (displayStatus === 'completed' ? 'Collected by Owner' : 'Owner Collecting')}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Horizontal Progress Stepper */}
                        <div className="mt-4 mb-2 px-1">
                          <div className="flex items-center justify-between relative">
                            {/* Background line */}
                            <div className="absolute top-[11px] left-[16px] right-[16px] h-[2px] bg-border" />
                            {/* Active line */}
                            {activeStepIdx >= 0 && (
                              <motion.div
                                className={`absolute top-[11px] left-[16px] h-[2px] ${statusInfo.barColor}`}
                                initial={false}
                                animate={{
                                  width: `${stages.length > 1 ? (Math.min(activeStepIdx, stages.length - 1) / (stages.length - 1)) * 100 : 0}%`
                                }}
                                style={{ maxWidth: 'calc(100% - 32px)' }}
                                transition={{ duration: 0.8, ease: 'easeOut' }}
                              />
                            )}
                            
                            {stages.map((stage, i) => {
                              const StageIcon = stage.icon;
                              const isComplete = i < activeStepIdx || (i === activeStepIdx && displayStatus === 'completed');
                              const isActive = i === activeStepIdx && displayStatus !== 'completed' && displayStatus !== 'pending';
                              const isFuture = i > activeStepIdx || displayStatus === 'pending';
                              
                              return (
                                <div key={stage.key} className="flex flex-col items-center z-10 relative" style={{ flex: '1 1 0' }}>
                                  <motion.div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                                      isComplete
                                        ? 'bg-[hsl(var(--success))] text-white shadow-sm'
                                        : isActive
                                          ? `${statusInfo.barColor} text-white shadow-md ring-4 ring-primary/20`
                                          : 'bg-background border-2 border-border text-muted-foreground'
                                    }`}
                                    animate={isActive ? { scale: [1, 1.15, 1] } : {}}
                                    transition={isActive ? { repeat: Infinity, duration: 2, ease: 'easeInOut' } : {}}
                                  >
                                    {isComplete ? (
                                      <CheckCircle className="w-3.5 h-3.5" />
                                    ) : (
                                      <StageIcon className="w-3 h-3" />
                                    )}
                                  </motion.div>
                                  <span className={`text-[10px] mt-1.5 font-medium text-center leading-tight ${
                                    isComplete ? 'text-[hsl(var(--success))]' :
                                    isActive ? 'text-primary font-semibold' :
                                    'text-muted-foreground/60'
                                  }`}>
                                    {stage.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Expand/collapse toggle */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
                          className="w-full flex items-center justify-center gap-1.5 pt-2 pb-1 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                          data-testid={`pet-expand-toggle-${index}`}
                        >
                          <span>{isExpanded ? 'Hide Journey Details' : 'View Journey Details'}</span>
                          <motion.div
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </motion.div>
                        </button>
                      </div>

                      {/* Expandable: Journey Timeline + Next Step */}
                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                            className="overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="px-5 pb-5 pt-2 border-t border-border/50">
                              {/* Vertical Connected Timeline */}
                              <div className="mt-4 ml-1">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Pet Journey</h4>
                                <div className="relative pl-8">
                                  {/* Vertical connecting line */}
                                  <div className="absolute left-[11px] top-1 bottom-1 w-[2px] bg-border" />
                                  
                                  <div className="space-y-5">
                                    {/* Step 1: Submitted — use CRM's submitted_at.aest, fallback to our MongoDB submission_date, last resort arrival_date */}
                                    <TimelineStep
                                      icon={Clock}
                                      label="Submitted"
                                      time={formatDateTime(pet.submitted_at?.aest || pet.submission_date || pet.arrival_date)}
                                      status="complete"
                                    />

                                    {/* Step 1b: Collection/Delivery Scheduled — for pending pets */}
                                    {displayStatus === 'pending' && pet.arrival_date && !isSelfDelivery && (
                                      <TimelineStep
                                        icon={Calendar}
                                        label="Collection Scheduled"
                                        time={formatDate(pet.arrival_date)}
                                        status="active"
                                      />
                                    )}
                                    {displayStatus === 'pending' && isSelfDelivery && (
                                      <TimelineStep
                                        icon={Truck}
                                        label="Delivery Scheduled"
                                        time={pet.planned_delivery_datetime ? formatLocalDateTime(pet.planned_delivery_datetime) : formatDate(pet.arrival_date)}
                                        status="active"
                                      />
                                    )}
                                    
                                    {/* Step 2: Collected / Delivered by Clinic */}
                                    {(pet.collected_at || events.some(e => e.event_type === 'pet_collected_from_clinic') || displayStatus !== 'pending') && (
                                      <TimelineStep
                                        icon={Truck}
                                        label={isSelfDelivery ? 'Delivered by Clinic' : 'Collected by Driver'}
                                        time={pet.collected_at?.aest
                                          ? formatDateTime(pet.collected_at.aest)
                                          : events.find(e => e.event_type === 'pet_collected_from_clinic')
                                            ? formatEventTime(events.find(e => e.event_type === 'pet_collected_from_clinic'))
                                            : isSelfDelivery && pet.planned_delivery_datetime
                                              ? formatLocalDateTime(pet.planned_delivery_datetime)
                                              : formatDateTime(pet.arrival_date)}
                                        status={activeStepIdx > 0 || displayStatus === 'completed' ? 'complete' : activeStepIdx === 0 ? 'active' : 'pending'}
                                      />
                                    )}

                                    {/* Step 3: In Care */}
                                    {(pet.in_care_at || events.some(e => e.event_type === 'pet_in_care') ||
                                      ['in_care', 'cremation', 'cremation_in_progress', 'ashes_ready', 'awaiting_delivery_to_vet', 'delivered_to_vet', 'completed', 'delivered'].includes(displayStatus)) && (
                                      <TimelineStep
                                        icon={Heart}
                                        label="Arrived at Pure Souls"
                                        time={pet.in_care_at?.aest
                                          ? formatDateTime(pet.in_care_at.aest)
                                          : events.find(e => e.event_type === 'pet_in_care')
                                            ? formatEventTime(events.find(e => e.event_type === 'pet_in_care'))
                                            : formatDateTime(pet.arrival_date)}
                                        status={activeStepIdx > 1 || displayStatus === 'completed' ? 'complete' : activeStepIdx === 1 ? 'active' : 'pending'}
                                      />
                                    )}

                                    {/* Step 4: Aquacremation */}
                                    {(pet.cremation_started_at || events.some(e => e.event_type === 'pet_cremation_started') ||
                                      ['cremation', 'cremation_in_progress', 'ashes_ready', 'awaiting_delivery_to_vet', 'delivered_to_vet', 'completed', 'delivered'].includes(displayStatus)) && (
                                      <TimelineStep
                                        icon={Droplet}
                                        label="Aquacremation Underway"
                                        time={pet.cremation_started_at?.aest
                                          ? formatDateTime(pet.cremation_started_at.aest)
                                          : events.find(e => e.event_type === 'pet_cremation_started')
                                            ? formatEventTime(events.find(e => e.event_type === 'pet_cremation_started'))
                                            : formatDateTime(pet.last_updated)}
                                        status={activeStepIdx > 2 || displayStatus === 'completed' ? 'complete' : activeStepIdx === 2 ? 'active' : 'pending'}
                                      />
                                    )}

                                    {/* Step 5: Delivery steps — ONLY if ashes_delivery_to_vet */}
                                    {ashesToVet && (displayStatus === 'awaiting_delivery_to_vet') && (
                                      <TimelineStep
                                        icon={Package}
                                        label="Ashes Ready — Delivery Coming"
                                        time={formatDateTime(pet.last_updated)}
                                        status="active"
                                      />
                                    )}

                                    {ashesToVet && (pet.delivered_at || events.some(e => e.event_type === 'ashes_delivered_to_clinic') ||
                                      ['delivered_to_vet', 'delivered'].includes(displayStatus) ||
                                      (displayStatus === 'completed' && pet.delivered_at)) && (
                                      <TimelineStep
                                        icon={Package}
                                        label="Ashes Delivered to Clinic"
                                        time={pet.delivered_at?.aest
                                          ? formatDateTime(pet.delivered_at.aest)
                                          : events.find(e => e.event_type === 'ashes_delivered_to_clinic')
                                            ? formatEventTime(events.find(e => e.event_type === 'ashes_delivered_to_clinic'))
                                            : formatDateTime(pet.last_updated)}
                                        status={displayStatus === 'completed' ? 'complete' : 'active'}
                                      />
                                    )}

                                    {/* Step 6: Complete */}
                                    {(pet.completed_at || displayStatus === 'completed') && (
                                      <TimelineStep
                                        icon={CheckCircle}
                                        label={!ashesToVet ? 'Completed (Owner reunited with pet)' : 'Completed'}
                                        time={pet.completed_at?.aest
                                          ? formatDateTime(pet.completed_at.aest)
                                          : formatDateTime(pet.last_updated)}
                                        status="complete"
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Next Step */}
                              {displayStatus !== 'completed' && (
                                <div className="mt-5 bg-primary/5 rounded-xl p-4 border-l-4 border-primary">
                                  <p className="text-sm">
                                    <span className="font-semibold">Next Step: </span>
                                    <span className="text-muted-foreground">{statusInfo.nextStep}</span>
                                  </p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* Pre-booked section — collapsible, shown below active pets */}
            {filteredPets.filter(p => p.workflow_stage === 'prepayment').length > 0 && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setPreBookedOpen(!preBookedOpen)}
                  className="w-full flex items-center justify-between p-4 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/20 hover:bg-muted/40 transition-all cursor-pointer"
                  data-testid="prebooked-toggle"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-sm text-muted-foreground" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Pre-booked — Awaiting Euthanasia &nbsp;·&nbsp; {filteredPets.filter(p => p.workflow_stage === 'prepayment').length} pets
                    </span>
                  </div>
                  <motion.div animate={{ rotate: preBookedOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {preBookedOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden mt-2 space-y-3"
                    >
                      {filteredPets.filter(p => p.workflow_stage === 'prepayment').map((pet, index) => {
                        const petId = pet.pet_id || pet.pet_record_id;
                        const petKey = petId || `prebooked-${index}`;
                        const statusInfo = getStatusInfo('prepayment', pet);
                        const isExpanded = expandedPets.has(petKey);
                        const toggleExpand = () => {
                          setExpandedPets(prev => {
                            const next = new Set(prev);
                            if (next.has(petKey)) next.delete(petKey); else next.add(petKey);
                            return next;
                          });
                        };
                        return (
                          <PreBookedCard
                            key={petKey}
                            pet={pet}
                            petKey={petKey}
                            index={index}
                            statusInfo={statusInfo}
                            isExpanded={isExpanded}
                            toggleExpand={toggleExpand}
                            onActivated={() => fetchPetsStatus(false)}
                          />
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

// Pre-booked card with Activate Collection form
const PreBookedCard = ({ pet, petKey, index, statusInfo, isExpanded, toggleExpand, onActivated }) => {
  const [activating, setActivating] = useState(false);
  const [showActivateForm, setShowActivateForm] = useState(false);
  const [notes, setNotes] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [emotionalState, setEmotionalState] = useState('');

  const handleActivate = async () => {
    const petId = pet.pet_id || pet.pet_record_id;
    setActivating(true);
    try {
      const token = localStorage.getItem('vet_portal_token');
      const payload = { urgent };
      if (notes) payload.collection_notes = notes;
      if (emotionalState) payload.owner_emotional_state = emotionalState;
      const rawRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pets/activate-prepayment/${petId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      if (rawRes.status === 401) {
        localStorage.removeItem('vet_portal_token');
        localStorage.removeItem('vet_portal_clinic');
        localStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return;
      }
      const res = rawRes.clone();
      const data = await res.json();
      if (res.ok) {
        toast.success(`${pet.pet_name} activated for collection!`, { description: 'The pet has been moved to active collection.' });
        setShowActivateForm(false);
        onActivated();
      } else {
        toast.error(getErrorMessage(data.detail, 'Activation failed'));
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setActivating(false);
    }
  };

  const emotionalOptions = [
    { value: 'very_distressed', label: 'Very Distressed', dot: 'bg-red-500', ring: 'border-red-300 bg-red-50', inactive: 'border-border hover:border-red-200' },
    { value: 'emotional',       label: 'Emotional',       dot: 'bg-orange-400', ring: 'border-orange-300 bg-orange-50', inactive: 'border-border hover:border-orange-200' },
    { value: 'calm',            label: 'Calm',            dot: 'bg-green-500', ring: 'border-green-300 bg-green-50', inactive: 'border-border hover:border-green-200' },
    { value: 'not_present',     label: 'Not Present',     dot: 'bg-gray-400', ring: 'border-gray-300 bg-gray-50', inactive: 'border-border hover:border-gray-200' },
    { value: 'unsure',          label: 'Unsure',          dot: 'bg-slate-400', ring: 'border-slate-300 bg-slate-50', inactive: 'border-border hover:border-slate-200' },
  ];

  return (
    <motion.div
      key={petKey}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.04 }}
      data-testid={`prebooked-card-${index}`}
    >
      <Card className="border border-dashed border-muted-foreground/20 shadow-sm hover:opacity-100 transition-opacity">
        <div className="h-1 bg-muted-foreground/20 rounded-t-2xl" />
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <PawPrint className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold truncate text-muted-foreground" style={{ fontFamily: 'Fredoka, sans-serif' }}>{pet.pet_name}</h3>
                <p className="text-xs text-muted-foreground/70 truncate">
                  <User className="h-3 w-3 inline mr-1 -mt-0.5" />{pet.client_name}
                  {pet.phone_number && <span className="ml-2 opacity-60">· {pet.phone_number}</span>}
                </p>
              </div>
            </div>
            <Badge className={`${statusInfo.className} border font-medium px-3 py-1 text-xs flex-shrink-0`}>
              <Clock className="h-3 w-3 mr-1.5" />Pre-booked
            </Badge>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {pet.breed && <div className="bg-muted/40 rounded-md px-2.5 py-1"><span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mr-1.5">Breed</span><span className="text-xs font-medium">{pet.breed}</span></div>}
            {pet.weight_kg && <div className="bg-muted/40 rounded-md px-2.5 py-1"><span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mr-1.5">Weight</span><span className="text-xs font-medium">{pet.weight_kg} kg</span></div>}
          </div>

          {/* Activate Collection button */}
          {!showActivateForm ? (
            <button
              type="button"
              onClick={() => setShowActivateForm(true)}
              className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary font-semibold text-sm transition-all border border-primary/20 hover:border-primary/40 cursor-pointer"
              data-testid={`activate-btn-${index}`}
            >
              <CheckCircle className="h-4 w-4" />
              Activate Collection
            </button>
          ) : (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mt-3 border-t border-border/40 pt-3 space-y-3"
                onClick={e => e.stopPropagation()}
              >
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Activate Collection — Optional Details</p>

                {/* Notes */}
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Collection notes (e.g. owner is at the clinic now)..."
                  rows={2}
                  className="w-full text-sm border border-border rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 bg-background"
                  data-testid={`activate-notes-${index}`}
                />

                {/* Emotional State */}
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground font-medium">Owner Emotional State</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                    {emotionalOptions.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEmotionalState(emotionalState === opt.value ? '' : opt.value)}
                        className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all text-xs cursor-pointer ${emotionalState === opt.value ? opt.ring + ' border-2' : opt.inactive + ' border'}`}
                      >
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${opt.dot}`} />
                        <span className="font-medium truncate">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Urgent toggle */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={() => setUrgent(!urgent)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${urgent ? 'bg-destructive' : 'bg-muted'}`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${urgent ? 'translate-x-5' : ''}`} />
                  </div>
                  <span className="text-sm font-medium">Emergency Collection</span>
                </label>

                {/* Action buttons */}
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={handleActivate}
                    disabled={activating}
                    className="flex-1 gap-2"
                    data-testid={`activate-confirm-${index}`}
                  >
                    {activating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    {activating ? 'Activating...' : 'Confirm Activation'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowActivateForm(false)} className="px-4">
                    Cancel
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

// Timeline step sub-component for the expanded view
const TimelineStep = ({ icon: Icon, label, time, status }) => {
  const isComplete = status === 'complete';
  const isActive = status === 'active';
  
  return (
    <div className="relative flex items-start gap-3">
      {/* Dot on the vertical line */}
      <div className={`absolute -left-8 top-0.5 w-[22px] h-[22px] rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
        isComplete
          ? 'bg-[hsl(var(--success))] text-white'
          : isActive
            ? 'bg-primary text-white ring-4 ring-primary/20'
            : 'bg-background border-2 border-border text-muted-foreground'
      }`}>
        <Icon className="w-3 h-3" />
      </div>
      <div className="flex-1 pb-0.5">
        <p className={`text-sm font-medium ${isComplete || isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
          {label}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{time}</p>
      </div>
    </div>
  );
};

export default PetTracking;
